/**
 * Indonesian — the source of truth.
 *
 * Keys are namespaced by where they appear. `en.ts` is typed against this
 * object, so adding a key here and forgetting it there is a compile error
 * rather than an English screen with an Indonesian sentence in the middle.
 *
 * Placeholders are {braced} and substituted by `t(key, vars)`.
 */
export const id = {
  // --- Shared chrome ---
  'app.loading': 'Memuat…',
  'app.quotaLeft': 'Kuota baris',
  'nav.accurate': 'Koneksi Accurate',
  'nav.team': 'Pengguna',
  'nav.billing': 'Langganan & Kuota',
  'nav.account': 'Akun & Kata Sandi',
  'nav.logout': 'Keluar',
  'nav.loggingOut': 'Keluar…',
  'quota.exhausted': 'Kuota baris bulan ini habis — penerimaan baru akan ditolak.',
  'quota.upgrade': 'Upgrade paket',
  'quota.askAdmin': 'Hubungi admin untuk upgrade paket.',
  'nav.backAria': 'Kembali',

  // --- Common actions and words ---
  'common.save': 'Simpan',
  'common.cancel': 'Batal',
  'common.close': 'Tutup',
  'common.back': 'Kembali',
  'common.add': 'Tambah',
  'common.delete': 'Hapus',
  'common.edit': 'Ubah',
  'common.search': 'Cari',
  'common.loading': 'Memuat…',
  'common.saving': 'Menyimpan…',
  'common.yes': 'Ya',
  'common.no': 'Tidak',
  'common.active': 'Aktif',
  'common.inactive': 'Nonaktif',
  'common.required': 'Wajib diisi',
  'common.unknownError': 'Terjadi kesalahan. Silakan coba lagi.',

  // --- Roles ---
  'role.owner': 'Pemilik',
  'role.admin': 'Admin',
  'role.supervisor': 'Supervisor',
  'role.staff': 'Staf',

  // --- Login and signup ---
  'login.title': 'Masuk',
  'login.signupTitle': 'Daftar Akun Baru',
  'login.tagline': 'Penerimaan barang gudang, terhubung langsung ke Accurate Online',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Masuk',
  'login.submitting': 'Memproses…',
  'login.noAccount': 'Belum punya akun?',
  'login.signupLink': 'Daftar gratis',
  'login.haveAccount': 'Sudah punya akun?',
  'login.loginLink': 'Masuk di sini',
  'signup.company': 'Nama Perusahaan',
  'signup.name': 'Nama Anda',
  'signup.submit': 'Daftar',

  // --- Database picker and menu ---
  'home.pickDatabase': 'Pilih Database',
  'home.historyOnly': 'Tidak terhubung · riwayat saja',
  'home.connectionProblem': 'Koneksi bermasalah',
  'menu.scan': 'Scan Baru',
  'menu.drafts': 'Lanjutkan Draft',
  'menu.history': 'Daftar Penerimaan',
  'menu.notFound': 'Database tidak ditemukan atau Anda tidak punya akses.',
  'menu.disconnected':
    'Database ini sudah tidak terhubung ke Accurate. Riwayat penerimaannya tetap bisa dibuka dan dicetak, tetapi penerimaan baru tidak bisa dibuat sampai koneksinya dipasang kembali di Pengaturan.',
  // --- Shared status and role labels ---
  'role.adminDesc': 'Admin — kelola pengguna dan koneksi',
  'role.supervisorDesc': 'Supervisor — scan, kirim, dan batalkan penerimaan',
  'role.staffDesc': 'Staf — scan dan kirim penerimaan',
  'platformRole.superadmin': 'Superadmin',
  'platformRole.admin': 'Admin',
  'receiptStatus.posted': 'Terkirim',
  'receiptStatus.pending': 'Diproses',
  'receiptStatus.failed': 'Gagal',
  'receiptStatus.voided': 'Dibatalkan',
  'billingStatus.trialing': 'Uji coba',
  'billingStatus.active': 'Aktif',
  'billingStatus.pastDue': 'Tertunggak',
  'billingStatus.cancelled': 'Dibatalkan',
  'billingStatus.paid': 'Lunas',
  'billingStatus.pending': 'Menunggu',
  'billingStatus.failed': 'Gagal',
  'billingStatus.expired': 'Kedaluwarsa',
  'billingStatus.refunded': 'Dikembalikan',
  // --- GRN print ---
  'grn.loadFailed': 'Gagal memuat dokumen.',
  'grn.preparing': 'Menyiapkan dokumen…',
  'grn.printTip': 'Tip: matikan opsi {option} di dialog cetak agar hasilnya bersih.',
  'grn.print': 'Cetak',
  'grn.subtitle': 'Bukti Penerimaan Barang Gudang',
  'grn.badge': 'Good Receive Note',
  'grn.vendor': 'Pemasok',
  'grn.poNumber': 'No. Pesanan Pembelian',
  'grn.notes': 'Keterangan Terima',
  'grn.grNumber': 'No. Penerimaan',
  'grn.date': 'Tanggal Penerimaan',
  'grn.deliveryNote': 'No. Surat Jalan',
  'grn.warehouse': 'Gudang Terima',
  'grn.continued': 'lanjutan',
  'grn.colNo': 'No',
  'grn.colSku': 'Kode Barang',
  'grn.colBarcode': 'Barcode / UPC',
  'grn.colItem': 'Detail Barang',
  'grn.colQty': 'Kts Diterima',
  'grn.colUnit': 'Satuan',
  'grn.colNotes': 'Catatan Barang',
  'grn.total': 'Total',
  'grn.receivedBy': 'Diterima Oleh,',
  'grn.receivedBySub': '(Petugas Gudang)',
  'grn.deliveredBy': 'Diserahkan Oleh,',
  'grn.deliveredBySub': '(Kurir Pengirim)',
  'grn.createdBy': 'Dibuat oleh {name}',
  'grn.accurateNo': 'No. Accurate: {number}',
  'grn.signName': 'Nama',
  'grn.signDate': 'Tanggal',
  'grn.notPrintable':
    'Penerimaan {number} berstatus "{status}", jadi GRN-nya tidak bisa dicetak. Hanya penerimaan yang sudah tercatat di Accurate yang boleh ditandatangani.',
  // --- Receipt detail modal ---
  'detail.title': 'Detail Penerimaan',
  'detail.voidTitle': 'Batalkan Penerimaan',
  'detail.loading': 'Memuat detail…',
  'detail.loadFailed': 'Gagal memuat detail.',
  'detail.voidFailed': 'Gagal membatalkan penerimaan.',
  'detail.voidWarning':
    'Dokumen {number} akan dihapus dari Accurate. Stok dan sisa pesanan pada PO {po} kembali seperti sebelum penerimaan ini. Catatan di aplikasi ini tetap tersimpan sebagai "Dibatalkan", lengkap dengan alasannya.',
  'detail.voidReason': 'Alasan pembatalan',
  'detail.voidReasonHint':
    'Wajib diisi, minimal 5 karakter. Alasan ini tersimpan permanen pada catatan penerimaan.',
  'detail.voidReasonPlaceholder': 'Contoh: salah input gudang, barang dikembalikan ke pemasok',
  'detail.voiding': 'Membatalkan…',
  'detail.voidConfirm': 'Ya, batalkan penerimaan',
  'detail.failReason': 'Alasan gagal dari Accurate',
  'detail.failNoReason': 'Penerimaan ini gagal, tetapi Accurate tidak mengirim alasannya.',
  'detail.voidedLabel': 'Dibatalkan',
  'detail.voidedNoReason': 'Tanpa alasan tercatat.',
  'detail.voidedBy': 'oleh {name}',
  'detail.receiptNo': 'No penerimaan',
  'detail.status': 'Status',
  'detail.date': 'Tanggal',
  'detail.poNo': 'No PO',
  'detail.vendor': 'Pemasok',
  'detail.branch': 'Cabang',
  'detail.warehouse': 'Gudang',
  'detail.deliveryNote': 'Surat jalan',
  'detail.database': 'Database',
  'detail.createdBy': 'Dibuat oleh',
  'detail.accurateNo': 'No di Accurate',
  'detail.notes': 'Keterangan',
  'detail.items': 'Barang ({count})',
  'detail.colItem': 'Barang',
  'detail.colOrdered': 'Pesan',
  'detail.colReceived': 'Terima',
  'detail.colResult': 'Hasil',
  // --- Receipt history ---
  'history.loadFailed': 'Gagal memuat riwayat.',
  'history.loading': 'Memuat riwayat…',
  'history.search': 'Pencarian',
  'history.searchPlaceholder': 'No penerimaan, pemasok, surat jalan, no PO…',
  'history.monthFilter': 'Filter Bulan',
  'history.empty': 'Data tidak ditemukan.',
  'history.colNo': 'NO',
  'history.colDate': 'Tanggal',
  'history.colReceipt': 'No Penerimaan',
  'history.colVendor': 'Pemasok',
  'history.colDeliveryNote': 'Surat Jalan',
  'history.colWarehouse': 'Gudang',
  'history.colCreatedBy': 'Dibuat Oleh',
  'history.colItems': 'Item (Kts)',
  'history.colStatus': 'Status',
  'history.colActions': 'Aksi',
  'history.viewDetail': 'Lihat detail',
  'history.viewDetailFor': 'Lihat detail {number}',
  'history.printGrn': 'Cetak GRN',
  'history.printGrnFor': 'Cetak GRN {number}',
  // --- Accurate connection settings ---
  'acc.title': 'Koneksi Accurate Online',
  'acc.connected': 'Terhubung',
  'acc.notConnected': 'Belum terhubung',
  'acc.connectedOk': 'Akun Accurate berhasil terhubung.',
  'acc.statusFailed': 'Gagal memuat status koneksi.',
  'acc.startFailed': 'Gagal memulai proses koneksi.',
  'acc.listFailed': 'Gagal mengambil daftar database.',
  'acc.connectDbFailed': 'Gagal menghubungkan database.',
  'acc.saveFailed': 'Gagal menyimpan perubahan.',
  'acc.disconnectFailed': 'Gagal memutuskan koneksi.',
  'acc.removeDbFailed': 'Gagal memutuskan database.',
  'acc.account': 'Akun Accurate',
  'acc.validUntil': 'Koneksi berlaku hingga',
  'acc.lastRefreshed': 'Terakhir diperbarui',
  'acc.never': 'Belum pernah',
  'acc.autoRefresh':
    'Koneksi diperbarui otomatis sebelum kedaluwarsa. Anda tidak perlu menghubungkan ulang kecuali akses dicabut dari sisi Accurate.',
  'acc.disconnecting': 'Memutuskan…',
  'acc.disconnect': 'Putuskan Koneksi',
  'acc.redirecting': 'Mengalihkan…',
  'acc.connectAccount': 'Hubungkan Akun Accurate',
  'acc.connectedDatabases': 'Database Terhubung',
  'acc.noDatabases': 'Belum ada database yang dipakai.',
  'acc.accurateId': 'ID Accurate {id}',
  'acc.editEdrc': 'Ubah ED RC',
  'acc.disconnectShort': 'Putuskan',
  'acc.processing': 'Memproses…',
  'acc.availableTitle': 'Tersedia di akun Accurate',
  'acc.alreadyConnected': 'Sudah terhubung',
  'acc.connect': 'Hubungkan',
  'acc.connecting': 'Menghubungkan…',
  'acc.viewPlans': 'Lihat Paket',
  'acc.upgradeNow': 'Upgrade paket sekarang →',
  'acc.connectTitle': 'Hubungkan "{alias}"',
  'acc.edrcField': 'Field ED RC',
  'acc.edrcCurrent': 'Saat ini: {value}',
  'acc.edrcHelp':
    'Pilih field kustom Accurate yang menyimpan ED RC (minimum sisa masa simpan, dalam bulan) di database ini. Setiap database bisa berbeda.',
  'acc.edrcEditTitle': 'Ubah Field ED RC — {alias}',
  'acc.edrcEditHelp':
    'Field kustom Accurate yang menyimpan ED RC (minimum sisa masa simpan, dalam bulan). Perubahan berlaku untuk penerimaan berikutnya; riwayat yang sudah tersimpan tidak berubah.',
  'acc.confirmDisconnectTitle': 'Putuskan Koneksi Accurate?',
  'acc.confirmDisconnectYes': 'Ya, Putuskan',
  'acc.confirmDisconnectBody':
    '{count} database akan dilepas dan scanning berhenti sampai Anda menghubungkan ulang. Riwayat penerimaan tetap tersimpan.',
  'acc.confirmDisconnectBodyNone':
    'Token Accurate akan dihapus dan Anda perlu menghubungkan ulang untuk bekerja.',
  'acc.confirmDbReleased': 'Database dilepas',
  'acc.confirmHistoryKept': 'Riwayat penerimaan',
  'acc.confirmHistoryKeptValue': 'Tetap tersimpan',
  'acc.confirmRemoveTitle': 'Putuskan "{alias}"?',
  'acc.confirmRemoveBody':
    'Database ini dilepas dari akun Anda dan bisa dihubungkan kembali kapan saja — juga oleh akun lain. Riwayat penerimaan tetap tersimpan.',
  // --- Scan and submit ---
  'scan.warehousesFailed': 'Gagal memuat daftar gudang.',
  'scan.draftLoadFailed': 'Gagal memuat draft.',
  'scan.poFailed': 'Gagal memuat PO.',
  'scan.draftSaved': 'Draft berhasil disimpan.',
  'scan.draftSaveFailed': 'Gagal menyimpan draft.',
  'scan.submitFailed': 'Gagal mengirim penerimaan.',
  'scan.noDatabase': 'Database Accurate tidak ditemukan atau Anda tidak punya akses.',
  'scan.prepareTitle': 'Persiapan Dokumen Penerimaan',
  'scan.poLabel': 'Nomor Pesanan Pembelian',
  'scan.poPlaceholder': 'Scan atau ketik nomor PO, lalu tekan Enter',
  'scan.searching': 'Mencari…',
  'scan.findPo': 'Cari PO',
  'scan.contactingAccurate': 'Menghubungi Accurate…',
  'scan.vendorName': 'Nama Pemasok',
  'scan.shipAddress': 'Alamat Gudang Terima',
  'scan.shipAddressHint': 'Pastikan alamat sudah sesuai.',
  'scan.deliveryNote': 'Nomor Surat Jalan / Faktur Pemasok',
  'scan.warehouse': 'Gudang Terima',
  'scan.continue': 'Lanjutkan Proses',
  'scan.vendor': 'Pemasok',
  'scan.poNumber': 'No Pesanan',
  'scan.deliveryNoteShort': 'No Surat Jalan',
  'scan.barcodePlaceholder': 'Scan barcode barang…',
  'scan.tableSearch': 'Cari di tabel…',
  'scan.colItem': 'Detail Barang',
  'scan.colOrdered': 'Kts Pesan',
  'scan.colUnit': 'Satuan',
  'scan.colReceived': 'Kts Terima',
  'scan.colStatus': 'Status',
  'scan.colNotes': 'Catatan',
  'scan.editQty': 'Ubah kuantitas',
  'scan.emptyScanned': '— Belum ada barang di-scan —',
  'scan.emptySearch': '— Tidak ada hasil pencarian —',
  'scan.saveDraft': 'Simpan Draft',
  'scan.submit': 'Submit ke Accurate',
  'scan.confirmTitle': 'Konfirmasi Pengiriman',
  'scan.sending': 'Mengirim…',
  'scan.confirmSend': 'Ya, Kirim ke Accurate',
  'scan.lineCount': 'Jumlah Baris',
  'scan.totalQty': 'Total Kuantitas',
  'scan.receiveNotes': 'Keterangan Terima',
  'scan.receiveNotesHint': 'Teks ini tersimpan sebagai keterangan dokumen di Accurate Online.',
  'scan.receivedVia': 'Diterima via Inbound App oleh {name}',
  'scan.shortLines': '{count} baris diterima kurang dari jumlah pesanan.',
  'scan.barcodeNotInPo': 'Barcode {barcode} tidak ada di PO ini.',
  'scan.barcodeComplete': 'Semua variasi barcode {barcode} sudah diterima penuh.',
  'scan.pickVariant': 'Pilih Variasi Barang',
  'scan.qtyTitle': 'Input Kuantitas Penerimaan',
  'scan.saveItem': 'Simpan Item',
  'scan.openPhoto': 'Buka foto asli di tab baru',
  'scan.noPhoto': 'Tanpa foto',
  'scan.maxReceive': 'Maks. Terima',
  'scan.remaining': 'Sisa Pesanan',
  'scan.received': 'Diterima',
  'scan.edrcMonths': 'ED RC (bulan)',
  'scan.itemNotes': 'Catatan Produk',
  'scan.itemNotesPlaceholder': 'Mis. kemasan penyok, ED pendek…',
  'scan.qtyMin': 'Kuantitas minimal 1.',
  'scan.qtyMaxExisting': 'Total untuk baris ini maksimal {max} {unit}.',
  'scan.qtyMaxRemaining': 'Melebihi sisa pesanan — maksimal {max} {unit}.',
  'scan.alreadyInTable': 'Barang ini sudah ada di tabel ({qty} {unit}). Nilai di bawah akan menggantikannya.',
  'scan.fulfilmentComplete': 'Sesuai',
  'scan.fulfilmentShort': 'Kurang',
  'scan.itemCount': '{count} Barang ({qty} Kts)',
  'scan.confirmWarning':
    'Penerimaan akan dicatat di Accurate Online dan memotong sisa pesanan. Tindakan ini tidak dapat dibatalkan dari aplikasi ini.',
  'acc.connectHelp':
    'Hubungkan akun Accurate Online Anda. Anda akan diarahkan ke halaman resmi Accurate untuk memberikan izin — DR Inbound tidak pernah melihat password Accurate Anda.',
  'detail.voidAction': 'Batalkan Penerimaan',
  'role.restricted': 'Halaman ini hanya untuk {roles}. Hubungi admin perusahaan Anda jika Anda perlu akses.',
  'role.backHome': 'Kembali ke beranda',
  // --- Drafts ---
  'drafts.loadFailed': 'Gagal memuat draft.',
  'drafts.deleteFailed': 'Gagal menghapus draft.',
  'drafts.searching': 'Mencari draft…',
  'drafts.title': 'Draft Tersimpan',
  'drafts.empty': 'Tidak ada draft',
  'drafts.emptyHint': 'Semua dokumen di database ini sudah diselesaikan.',
  'drafts.deleting': 'Menghapus…',
  'drafts.confirmTitle': 'Hapus Draft?',
  'drafts.confirmBody': 'Draft ini akan dibuang dan tidak bisa dikembalikan.',
  'drafts.poNumber': 'No Pesanan',
  'drafts.vendor': 'Pemasok',
  'drafts.scanned': 'Sudah di-scan',
  'drafts.scannedValue': '{lines} baris · {qty} kts',
  'drafts.warehouse': 'Gudang',
  'drafts.vendorInline': 'Pemasok: {name}',
  'drafts.deliveryNoteInline': 'Surat jalan: {number}',

  // --- Account ---
  'account.failed': 'Gagal mengganti kata sandi.',
  'account.myAccount': 'Akun Saya',
  'account.email': 'Email',
  'account.role': 'Peran',
  'account.organisation': 'Organisasi',
  'account.changePassword': 'Ganti Kata Sandi',
  'account.changed': 'Kata sandi berhasil diganti.',
  'account.current': 'Kata sandi saat ini',
  'account.new': 'Kata sandi baru',
  'account.mustDiffer': 'Harus berbeda dari kata sandi lama.',
  'account.repeat': 'Ulangi kata sandi baru',
  'account.mismatch': 'Tidak sama.',

  // --- Email verification ---
  'verify.failed': 'Verifikasi gagal.',
  'verify.resendFailed': 'Gagal mengirim ulang.',
  'verify.verifying': 'Memverifikasi email…',
  'verify.verified': 'Email Terverifikasi',
  'verify.toAccount': 'Masuk ke Akun Anda',
  'verify.failedTitle': 'Verifikasi Gagal',
  'verify.toLogin': 'Ke Halaman Masuk',
  'verify.title': 'Verifikasi email Anda',
  'verify.sentTo': 'Kami mengirim tautan verifikasi ke {email}. Buka tautan tersebut untuk mengaktifkan akun dan memulai masa uji coba.',
  'verify.trialNote': 'Masa uji coba baru dimulai setelah email terverifikasi.',
  'verify.devMode': 'Mode pengembangan',
  'verify.devNote': 'Belum ada layanan email yang terpasang, jadi tautannya ditampilkan di sini:',
  'verify.sending': 'Mengirim…',
  'verify.resend': 'Kirim Ulang Tautan',

  // --- Mock checkout ---
  'mock.notFound': 'Tagihan tidak ditemukan.',
  'mock.markFailed': 'Gagal menandai lunas.',
  'mock.loading': 'Memuat tagihan…',
  'mock.banner': 'Halaman simulasi pembayaran — hanya untuk pengembangan',
  'mock.invoice': 'Tagihan',
  'mock.plan': 'Paket',
  'mock.price': 'Harga',
  'mock.total': 'Total',
  'mock.alreadyPaid': 'Tagihan ini sudah lunas.',
  'mock.processing': 'Memproses…',
  'mock.markPaid': 'Tandai Lunas (DEV)',

  // --- Common additions ---
  'common.saveFailed': 'Gagal menyimpan.',
  'common.never': 'Belum pernah',
  'common.you': '(Anda)',
  'common.processing': 'Memproses…',
  'common.email': 'Email',
  'common.password': 'Kata sandi',
  'common.newPassword': 'Kata sandi baru',

  // --- Confirm dialog defaults ---
  'confirm.acknowledge': 'Mengerti',
  'confirm.continue': 'Lanjutkan',

  // --- Team management ---
  'team.loadFailed': 'Gagal memuat daftar pengguna.',
  'team.changeSaved': 'Perubahan untuk {name} disimpan.',
  'team.saveChangeFailed': 'Gagal menyimpan perubahan.',
  'team.deactivateTitle': 'Nonaktifkan {name}?',
  'team.deactivate': 'Nonaktifkan',
  'team.activate': 'Aktifkan',
  'team.deactivateBody': 'Mereka langsung tidak bisa masuk. Riwayat penerimaan yang sudah dibuat tetap tersimpan atas nama mereka.',
  'team.seatUsedLabel': 'Kursi terpakai',
  'team.seatDecreases': 'berkurang satu',
  'team.reactivableLabel': 'Dapat diaktifkan lagi',
  'team.reactivableValue': 'ya, jika masih ada kursi',
  'team.heading': 'Pengguna',
  'team.activeUnlimited': '{count} pengguna aktif · tanpa batas',
  'team.seatsUsed': '{used} dari {max} kursi terpakai',
  'team.add': 'Tambah Pengguna',
  'team.atLimit': 'Semua kursi pada paket Anda sudah terpakai. Nonaktifkan pengguna yang tidak lagi bekerja, atau upgrade paket untuk menambah kursi.',
  'team.loadingUsers': 'Memuat pengguna…',
  'team.colName': 'Nama',
  'team.colEmail': 'Email',
  'team.colRole': 'Peran',
  'team.colLastLogin': 'Login terakhir',
  'team.colActions': 'Aksi',
  'team.resetPassword': 'Reset sandi',
  'team.userSaved': 'Data {name} disimpan.',
  'team.passwordReset': 'Kata sandi {name} diatur ulang. Minta mereka menggantinya di menu Akun.',
  'team.userAdded': '{name} ditambahkan.',
  'team.addFailed': 'Gagal menambahkan pengguna.',
  'team.adding': 'Menambahkan…',
  'team.emailHintNew': 'Dipakai untuk masuk. Harus belum terdaftar.',
  'team.initialPassword': 'Kata sandi awal',
  'team.initialPasswordHint': 'Sampaikan ke pengguna, dan minta mereka menggantinya setelah masuk.',
  'team.editTitle': 'Ubah Data Pengguna',
  'team.emailHintEdit': 'Dipakai untuk masuk. Mengubahnya mengubah cara mereka login.',
  'team.resetFailed': 'Gagal mengatur ulang kata sandi.',
  'team.resetTitle': 'Reset Kata Sandi — {name}',
  'team.resetSubmit': 'Reset kata sandi',
  'team.resetWarnPre': 'Sampaikan kata sandi ini ke {name} lewat jalur yang aman, dan minta mereka menggantinya sendiri di menu ',
  'team.resetWarnPost': '.',

  // --- Billing ---
  'billing.loadFailed': 'Gagal memuat data langganan.',
  'billing.invoiceCreatedTitle': 'Tagihan Dibuat',
  'billing.invoiceCreatedBody': 'Silakan lakukan pembayaran transfer dan kirim bukti ke tim DR Inbound untuk aktivasi paket Anda.',
  'billing.invoiceNo': 'No Tagihan',
  'billing.checkoutFailed': 'Gagal membuat tagihan.',
  'billing.loading': 'Memuat langganan…',
  'billing.heading': 'Langganan & Penggunaan',
  'billing.readonlyNote': 'Anda dapat melihat paket dan pemakaian, tetapi pembelian dan perpanjangan dilakukan oleh pemilik akun.',
  'billing.trialPeriod': 'Masa uji coba',
  'billing.activePlan': 'Paket aktif',
  'billing.freeTrial': 'Uji Coba Gratis',
  'billing.trialEnds': 'Berakhir {date}',
  'billing.quotaResets': 'Kuota direset {date}',
  'billing.activeUntil': 'aktif sampai {date}',
  'billing.rowsUsed': 'Baris terpakai',
  'billing.trialQuotaNote': 'Kuota uji coba {rows} baris — cukup untuk mencoba alur penerimaan. Pilih paket di bawah untuk kuota penuh.',
  'billing.exhausted': 'Kuota bulan ini habis. Penerimaan baru akan ditolak sampai Anda upgrade paket atau periode berikutnya dimulai.',
  'billing.warnRemaining': 'Sisa {rows} baris bulan ini. Pertimbangkan upgrade agar penerimaan tidak terhenti.',
  'billing.perMonth': '/bulan',
  'billing.taxLine': '+ PPN {tax} — total {total}',
  'billing.rowsPerMonth': 'baris produk / bulan',
  'billing.rowsPerDay': 'baris / hari kerja',
  'billing.dbUnlimited': 'Database Accurate tanpa batas',
  'billing.dbCount': 'database Accurate',
  'billing.usersUnlimited': 'Pengguna tanpa batas',
  'billing.usersCount': 'pengguna',
  'billing.renew': 'Perpanjang',
  'billing.choosePlan': 'Pilih Paket',
  'billing.ownerOnly': 'Hanya pemilik akun yang dapat membeli paket',
  'billing.invoiceHistory': 'Riwayat Tagihan',
  'billing.colPeriod': 'Periode',
  'billing.colTotal': 'Total',
  'billing.colStatus': 'Status',
  'billing.colPlan': 'Paket',
  'billing.pay': 'Bayar',
  'billing.pdfTitle': 'Unduh faktur {number} (PDF)',

  // --- Danger zone — account deletion ---
  'danger.scheduleFailed': 'Gagal menjadwalkan penghapusan.',
  'danger.cancelFailed': 'Gagal membatalkan penghapusan.',
  'danger.scheduledTitle': 'Akun Dijadwalkan Dihapus',
  'danger.scheduledAlertPre': 'Seluruh data organisasi ini akan dihapus permanen pada ',
  'danger.scheduledAlertPost': '. Koneksi Accurate sudah diputus dan aplikasi tidak bisa dipakai sampai penghapusan dibatalkan.',
  'danger.cancelling': 'Membatalkan…',
  'danger.cancelDeletion': 'Batalkan Penghapusan',
  'danger.heading': 'Hapus Akun',
  'danger.intro': 'Menghapus organisasi beserta seluruh pengguna, draft dan riwayat penerimaan. Koneksi Accurate diputus seketika; data dihapus permanen setelah masa tenggang.',
  'danger.deleteButton': 'Hapus Akun Organisasi',
  'danger.deleteTitle': 'Hapus Akun Organisasi',
  'danger.confirmDelete': 'Hapus Akun',
  'danger.warnPre': 'Tindakan ini menghapus ',
  'danger.warnBold': 'seluruh riwayat penerimaan',
  'danger.warnPost': ' organisasi ini. Setelah masa tenggang berakhir, data tidak dapat dipulihkan.',
  'danger.immediate': 'Yang terjadi seketika: koneksi Accurate diputus dan database dilepas sehingga bisa dipakai akun lain. Anda masih dapat membatalkan selama masa tenggang.',
  'danger.passwordLabel': 'Password Anda',
  'danger.passwordHint': 'Dikonfirmasi ulang untuk tindakan sepenting ini.',
  'danger.confirmNameLabel': 'Ketik nama perusahaan untuk konfirmasi',
  'danger.confirmNameHint': 'Persis: {name}',

  // --- Platform admin console ---
  'admin.navTenant': 'Tenant',
  'admin.navAdmin': 'Administrator',
  'admin.navPassword': 'Kata sandi',
  'admin.loginFailed': 'Gagal masuk.',
  'admin.loginTitle': 'Administrasi Platform',
  'admin.loginSubtitle': 'Area internal. Bukan halaman masuk pelanggan.',
  'admin.checking': 'Memeriksa…',
  'admin.signIn': 'Masuk',
  'admin.changePwFailed': 'Gagal mengganti kata sandi.',
  'admin.changePwTitle': 'Ganti Kata Sandi',
  'admin.forcedNote': 'Akun ini dibuat dengan kata sandi awal dari konfigurasi server. Ganti dulu sebelum dapat menggunakan area administrasi.',
  'admin.pwUpdated': 'Kata sandi diperbarui.',
  'admin.currentPassword': 'Kata sandi saat ini',
  'admin.repeatNewPassword': 'Ulangi kata sandi baru',
  'admin.mismatch': 'Tidak sama.',
  'admin.tenantsLoadFailed': 'Gagal memuat tenant.',
  'admin.searchTenants': 'Cari nama organisasi atau email pengguna…',
  'admin.loadingTenants': 'Memuat tenant…',
  'admin.noTenants': 'Tidak ada tenant yang cocok.',
  'admin.colOrg': 'Organisasi',
  'admin.colOwner': 'Pemilik',
  'admin.colPlan': 'Paket',
  'admin.colUsers': 'Pengguna',
  'admin.colQuota': 'Kuota',
  'admin.colStatus': 'Status',
  'admin.colCreated': 'Dibuat',
  'admin.deletedOn': 'dihapus {date}',
  'admin.actionFailed': 'Tindakan gagal.',
  'admin.deletionCancelled': 'Penghapusan dibatalkan.',
  'admin.cancelDeletion': 'Batalkan penghapusan',
  'admin.suspendTitle': 'Tangguhkan {name}?',
  'admin.suspend': 'Tangguhkan',
  'admin.suspendBody': 'Semua pengguna organisasi ini langsung tidak bisa masuk. Data tidak dihapus dan penangguhan bisa dicabut kapan saja.',
  'admin.suspensionLifted': 'Penangguhan dicabut.',
  'admin.tenantSuspended': 'Tenant ditangguhkan.',
  'admin.liftSuspension': 'Cabut penangguhan',
  'admin.scheduleDeleteTitle': 'Jadwalkan penghapusan {name}?',
  'admin.scheduleDelete': 'Jadwalkan penghapusan',
  'admin.scheduleDeleteBody': 'Koneksi Accurate langsung diputus dan database dilepas. Data dihapus permanen setelah masa tenggang 30 hari, dan bisa dibatalkan sebelum itu.',
  'admin.deleteReason': 'Dihapus oleh administrator platform',
  'admin.deletionScheduled': 'Penghapusan dijadwalkan.',
  'admin.deleteTenant': 'Hapus tenant',
  'admin.metaLine': '{email} · dibuat {date} · status ',
  'admin.scheduledPermanent': 'Dijadwalkan dihapus permanen pada {date}.',
  'admin.subscription': 'Langganan',
  'admin.rowsPerMonth': 'Baris/bulan',
  'admin.ends': 'Berakhir',
  'admin.noSubscription': 'Belum ada langganan.',
  'admin.databases': 'Database Accurate',
  'admin.notConnected': 'Belum terhubung.',
  'admin.released': '(lepas)',
  'admin.users': 'Pengguna',
  'admin.inactiveBadge': 'Nonaktif',
  'admin.unverifiedBadge': 'Belum verifikasi',
  'admin.userSuspended': 'Pengguna ditangguhkan.',
  'admin.userActivated': 'Pengguna diaktifkan.',
  'admin.invoices': 'Tagihan',
  'admin.noInvoices': 'Belum ada tagihan.',
  'admin.invoiceMarkedPaid': 'Tagihan {number} ditandai lunas.',
  'admin.markPaid': 'Tandai lunas',
  'admin.recentActivity': 'Aktivitas terakhir',
  'admin.adminsLoadFailed': 'Gagal memuat admin.',
  'admin.adminsHeading': 'Administrator Platform',
  'admin.adminsIntro': 'Superadmin dapat menandai tagihan lunas, menghapus tenant, dan mengelola admin lain.',
  'admin.addAdmin': 'Tambah Admin',
  'admin.mustChangeBadge': 'belum ganti sandi',
  'admin.roleChanged': 'Peran {name} diubah.',
  'admin.deactivateTitle': 'Nonaktifkan {name}?',
  'admin.deactivateBody': 'Mereka langsung kehilangan akses ke area administrasi.',
  'admin.adminDeactivated': 'Admin dinonaktifkan.',
  'admin.adminActivated': 'Admin diaktifkan.',
  'admin.deleteAdminTitle': 'Hapus {name}?',
  'admin.deletePermanent': 'Hapus permanen',
  'admin.deleteAdminBody': 'Akun admin ini dihapus permanen. Riwayat tindakannya di log audit tetap tersimpan.',
  'admin.adminDeleted': '{name} dihapus.',
  'admin.deleteFailed': 'Gagal menghapus.',
  'admin.userDataSaved': 'Data {name} disimpan.',
  'admin.passwordResetMustChange': 'Kata sandi {name} diatur ulang. Mereka wajib menggantinya saat login.',
  'admin.adminAddedNote': '{name} ditambahkan. Mereka harus mengganti kata sandi saat login pertama.',
  'admin.addAdminFailed': 'Gagal menambahkan admin.',
  'admin.addAdminTitle': 'Tambah Administrator',
  'admin.initialPasswordHint': 'Mereka wajib menggantinya saat login pertama.',
  'admin.editAdminTitle': 'Ubah Data Admin',
  'admin.adminEmailHint': 'Dipakai untuk masuk. Harus belum dipakai admin lain.',
  'admin.resetWarn': 'Sampaikan kata sandi ini ke {name} melalui jalur yang aman. Mereka wajib menggantinya saat login berikutnya, dan sesi lama mereka ikut terkunci sampai itu dilakukan.',

  // --- Tenant status badges ---
  'tenantStatus.active': 'aktif',
  'tenantStatus.trialing': 'uji coba',
  'tenantStatus.pastDue': 'jatuh tempo',
  'tenantStatus.suspended': 'ditangguhkan',
  'tenantStatus.closing': 'ditutup',
  'tenantStatus.pending': 'menunggu',
  'tenantStatus.cancelled': 'dibatalkan',

  // --- Server errors — fetch layer & generic ---
  'err.network': 'Koneksi ke server terputus. Periksa jaringan Anda.',
  'err.invalidResponse': 'Server mengembalikan respons tidak valid.',
  'err.requestFailed': 'Permintaan gagal (HTTP {status}).',
  'err.invalidData': 'Data yang dikirim tidak valid.',
  'err.system': 'Terjadi kesalahan sistem. Silakan coba lagi.',
  'err.notFoundEndpoint': 'Endpoint tidak ditemukan.',

  // --- Server errors — password strength ---
  'err.pw.tooShort': 'Password harus minimal 10 karakter.',
  'err.pw.tooLong': 'Password maksimal 200 karakter.',
  'err.pw.edgeSpace': 'Password tidak boleh diawali atau diakhiri spasi.',
  'err.pw.tooGuessable': 'Password terlalu mudah diduga.',

  // --- Server errors — authentication ---
  'err.auth.emailTakenLogin': 'Email ini sudah terdaftar. Silakan login.',
  'err.auth.badCredentials': 'Email atau password salah.',
  'err.auth.userInactive': 'Akun Anda belum diaktifkan. Hubungi admin perusahaan Anda.',
  'err.auth.tenantInactive': 'Akun organisasi Anda dinonaktifkan. Hubungi tim DR Inbound.',
  'err.auth.oldPasswordWrong': 'Password lama salah.',
  'err.auth.newPasswordSame': 'Password baru harus berbeda dari password lama.',
  'err.auth.passwordWrong': 'Password salah.',
  'err.auth.emailTaken': 'Email ini sudah terdaftar.',
  'err.auth.sessionExpired': 'Sesi berakhir. Silakan login kembali.',
  'err.auth.accountInactive': 'Akun Anda tidak aktif. Hubungi admin.',
  'err.auth.passwordChanged': 'Kata sandi akun ini telah diubah. Silakan login kembali.',
  'err.auth.tenantInactiveLong': 'Akun organisasi Anda dinonaktifkan. Hubungi tim DR Inbound untuk mengaktifkan kembali.',
  'err.auth.verifyFirst': 'Verifikasi email Anda terlebih dahulu untuk mulai menggunakan aplikasi.',
  'err.auth.closingScheduled': 'Akun ini dijadwalkan untuk dihapus. Batalkan penghapusan di Pengaturan untuk menggunakan aplikasi kembali.',
  'err.auth.notLoggedIn': 'Belum login.',
  'err.auth.forbidden': 'Anda tidak punya hak akses untuk tindakan ini.',

  // --- Server errors — account, users, databases ---
  'err.account.notFound': 'Akun tidak ditemukan.',
  'err.account.nameMismatch': 'Nama perusahaan tidak cocok. Ketik persis: {name}',
  'err.user.cannotDeactivateSelf': 'Anda tidak bisa menonaktifkan akun sendiri.',
  'err.user.useAccountPageForSelf': 'Gunakan halaman Akun untuk mengganti kata sandi Anda sendiri.',
  'err.user.notFoundOrImmutable': 'Pengguna tidak ditemukan atau tidak bisa diubah.',
  'err.db.notFoundOrNoAccess': 'Database Accurate tidak ditemukan atau Anda tidak punya akses.',

  // --- Server errors — platform admin & verification ---
  'err.admin.sessionExpired': 'Sesi admin berakhir. Silakan login kembali.',
  'err.admin.inactive': 'Akun admin tidak aktif.',
  'err.admin.changePasswordFirst': 'Ganti kata sandi Anda terlebih dahulu.',
  'err.admin.superadminOnly': 'Tindakan ini hanya untuk superadmin.',
  'err.verify.invalidLink': 'Tautan verifikasi tidak valid atau sudah kedaluwarsa. Minta tautan baru.',

  // --- Server errors — platform admin actions ---
  'err.admin.badCredentials': 'Email atau kata sandi salah.',
  'err.admin.currentPasswordWrong': 'Kata sandi saat ini salah.',
  'err.admin.newPasswordSame': 'Kata sandi baru harus berbeda dari yang lama.',
  'err.platform.noSuperadminLeft': 'Tindakan ini akan menyisakan platform tanpa superadmin aktif. Tunjuk superadmin lain terlebih dahulu.',
  'err.platform.tenantNotFound': 'Tenant tidak ditemukan.',
  'err.platform.tenantClosing': 'Tenant ini sedang dalam proses penghapusan.',
  'err.platform.userNotFound': 'Pengguna tidak ditemukan.',
  'err.platform.invoiceNotFound': 'Tagihan tidak ditemukan.',
  'err.platform.adminEmailTaken': 'Email ini sudah terdaftar sebagai admin.',
  'err.platform.cannotChangeOwnRole': 'Anda tidak bisa mengubah peran atau status akun sendiri.',
  'err.platform.adminEmailUsed': 'Email ini sudah dipakai admin lain.',
  'err.platform.adminNotFound': 'Admin tidak ditemukan.',
  'err.platform.usePasswordMenuForSelf': 'Gunakan menu Kata sandi untuk mengganti kata sandi Anda sendiri.',
  'err.platform.cannotDeleteSelf': 'Anda tidak bisa menghapus akun sendiri.',

  // --- Server errors — Accurate connection ---
  'err.accurate.dbNotFound': 'Database Accurate tidak ditemukan untuk akun Anda.',
  'err.accurate.dbDisabled': 'Koneksi ke database “{alias}” sedang dinonaktifkan.',
  'err.accurate.noActiveConnection': 'Belum ada koneksi Accurate aktif. Hubungkan akun Accurate dulu.',
  'err.accurate.reconnectNeeded': 'Koneksi Accurate perlu dihubungkan ulang. Buka Pengaturan → Koneksi Accurate.',
  'err.accurate.unreadable': 'Koneksi Accurate tidak dapat dibaca (kunci enkripsi berubah). Hubungkan ulang akun Accurate.',
  'err.accurate.expiredManual': 'Koneksi Accurate kedaluwarsa dan tidak bisa diperbarui otomatis. Hubungkan ulang.',
  'err.accurate.connectionNotFound': 'Koneksi Accurate tidak ditemukan.',
  'err.accurate.refreshUnreachable': 'Accurate sedang tidak bisa dihubungi untuk memperbarui koneksi. Coba lagi sebentar lagi — koneksi Anda tidak perlu dihubungkan ulang.',
  'err.accurate.refreshFailed': 'Gagal memperbarui koneksi Accurate: {message}. Silakan hubungkan ulang.',
  'err.accurate.openUnreachable': 'Tidak dapat menghubungi Accurate untuk membuka database.',
  'err.accurate.openInvalidResponse': 'Accurate mengembalikan respons tidak valid saat membuka database.',
  'err.accurate.openFailed': 'Gagal membuka database Accurate: {reason}',
  'err.accurate.listUnreachable': 'Tidak dapat mengambil daftar database dari Accurate.',
  'err.accurate.listFailed': 'Gagal mengambil daftar database: {reason}',
  'err.accurate.rejected': 'Koneksi Accurate ditolak dan tidak bisa diperbarui otomatis. Hubungkan ulang akun Accurate di Pengaturan. Ini terjadi antara lain bila akun Accurate yang sama dihubungkan ulang dari aplikasi atau lingkungan lain.',
  'err.accurate.poNotFound': 'Pesanan pembelian “{po}” tidak ditemukan.',
  'err.accurate.dbClaimedByOther': 'Database Accurate “{alias}” sudah terhubung ke akun DR Inbound lain. Satu database hanya bisa dipakai oleh satu akun. Putuskan dari akun lama terlebih dahulu, atau hubungi tim DR Inbound.',
  'err.accurate.noChanges': 'Tidak ada perubahan yang dikirim.',
  'err.accurate.dbNotFoundShort': 'Database tidak ditemukan.',

  // --- Server errors — receiving ---
  'err.receipt.notFound': 'Penerimaan tidak ditemukan.',
  'err.receipt.alreadyVoided': 'Penerimaan {gr} sudah dibatalkan.',
  'err.receipt.priorInterrupted': 'Pengiriman {gr} terputus dan dokumennya tidak terbentuk di Accurate. Kuota sudah dikembalikan. Mulai ulang penerimaan agar mendapat nomor baru.',
  'err.receipt.priorProcessing': 'Pengiriman sebelumnya masih diproses. Tunggu sebentar lalu cek Daftar Penerimaan.',
  'err.receipt.priorFailed': 'Pengiriman ini sudah pernah gagal ({reason}). Mulai ulang penerimaan agar mendapat nomor baru.',
  'err.receipt.sendUnknown': 'Koneksi ke Accurate terputus saat mengirim {gr}, dan statusnya belum bisa dipastikan. JANGAN kirim ulang penerimaan ini — sistem akan memeriksanya sendiri. Buka Daftar Penerimaan beberapa menit lagi untuk melihat hasilnya.',
  'err.receipt.sendFailed': 'Pengiriman {gr} ke Accurate gagal dan dokumennya tidak terbentuk. Data tersimpan sebagai gagal dan kuota dikembalikan.',
  'err.receipt.rejected': 'Accurate menolak penerimaan: {message}. Data tersimpan sebagai gagal dengan nomor {gr} dan kuota dikembalikan.',
  'err.receipt.stillProcessingVoid': 'Penerimaan ini masih diproses. Tunggu sampai selesai sebelum membatalkan.',
  'err.receipt.onlyPostedVoidable': 'Hanya penerimaan yang berhasil terkirim yang bisa dibatalkan.',
  'err.receipt.docNotFoundInAccurate': 'Dokumen {gr} tidak ditemukan di Accurate. Mungkin sudah dihapus di sana. Hubungi admin untuk menyesuaikan catatan.',
  'err.receive.nothingScanned': 'Belum ada barang yang di-scan.',
  'err.receive.lineNotInPo': 'Baris barang tidak ada di PO {po} (id {id}). PO mungkin sudah berubah di Accurate — muat ulang PO.',
  'err.receive.lineDuplicated': 'Baris “{item}” dikirim lebih dari sekali.',
  'err.receive.qtyPositive': 'Kuantitas “{item}” harus lebih dari 0.',
  'err.receive.lineFull': '“{item}” sudah diterima penuh ({qty} {unit}).',
  'err.receive.lineOverRemaining': '“{item}”: sisa yang bisa diterima hanya {remaining} {unit}, bukan {qty}.',

  // --- Server errors — tenant, drafts, billing ---
  'err.tenant.notFound': 'Organisasi tidak ditemukan.',
  'err.tenant.deletionAlreadyScheduled': 'Penghapusan akun sudah dijadwalkan.',
  'err.tenant.noDeletionScheduled': 'Tidak ada penghapusan akun yang dijadwalkan.',
  'err.tenant.deletionByPlatform': 'Penghapusan akun ini dilakukan oleh tim DR Inbound dan tidak bisa dibatalkan dari sini. Hubungi tim DR Inbound.',
  'err.draft.notFound': 'Draft tidak ditemukan.',
  'err.draft.notFoundOrProcessed': 'Draft tidak ditemukan atau sudah diproses.',
  'err.invoice.notFound': 'Tagihan tidak ditemukan.',
  'err.invoice.notFoundNumbered': 'Tagihan {number} tidak ditemukan.',
  'err.ipaymu.unreachable': 'Tidak dapat menghubungi penyedia pembayaran.',
  'err.ipaymu.createFailed': 'Gagal membuat tagihan: {reason}',
  'err.limits.databases': 'Paket {plan} hanya mencakup {max} database Accurate (sudah terpakai {current}). Upgrade paket untuk menambah database.',
  'err.limits.seats': 'Paket {plan} hanya mencakup {max} pengguna aktif (sudah terpakai {current}). Nonaktifkan pengguna lain atau upgrade paket.',
  'err.limits.planNotFound': 'Paket “{code}” tidak ditemukan.',
  'err.usage.subscriptionInactive': 'Langganan Anda tidak aktif. Buka halaman Langganan untuk mengaktifkan kembali.',
  'err.usage.pastDue': 'Pembayaran langganan tertunggak. Selesaikan pembayaran untuk melanjutkan penerimaan.',
  'err.usage.quotaExceeded': 'Kuota bulan ini habis: terpakai {used} dari {limit} baris, transaksi ini butuh {needed} baris. Upgrade paket untuk melanjutkan.',
  'err.usage.quotaExhausted': 'Kuota baris bulan ini sudah habis. Upgrade paket untuk melanjutkan penerimaan.',

  // --- Server errors — field validation (Zod) ---
  'err.v.emailFormat': 'Format email tidak valid',
  'err.v.passwordRequired': 'Password wajib diisi',
  'err.v.companyNameMin': 'Nama perusahaan minimal 2 karakter',
  'err.v.nameMin': 'Nama minimal 2 karakter',
  'err.v.reasonRequired': 'Alasan wajib diisi',
  'err.v.poRequired': 'Nomor PO wajib diisi',
  'err.v.monthFormat': 'Format bulan harus YYYY-MM',
  'err.v.voidReasonMin': 'Alasan pembatalan minimal 5 karakter',
  'err.v.deliveryNoteRequired': 'Nomor surat jalan wajib diisi',
  'err.v.nothingScanned': 'Belum ada barang yang di-scan',
  'err.v.idempotencyUuid': 'idempotencyKey harus UUID',
  'err.v.edRcField': 'Field ED RC harus numericField1..numericField10',

  // --- Server success messages ---
  'ok.emailVerifiedTrial': 'Email terverifikasi. Masa uji coba Anda dimulai sekarang.',
  'ok.emailVerified': 'Email terverifikasi.',
  'ok.emailAlreadyVerified': 'Email Anda sudah terverifikasi.',
  'ok.verificationResent': 'Tautan verifikasi baru telah dikirim.',
  'ok.account.deletionScheduled': 'Akun dijadwalkan dihapus permanen pada {date}. Koneksi Accurate sudah diputus. Anda dapat membatalkan sebelum tanggal tersebut.',
  'ok.accurate.disconnected': 'Koneksi Accurate diputus.',
  'ok.accurate.disconnectedWithDb': 'Koneksi Accurate diputus. {count} database dilepas.',

  // --- Signup form hints ---
  'signup.passwordHint': 'Minimal 10 karakter. Kami akan mengirim tautan verifikasi ke email Anda.',
  'signup.companyPlaceholder': 'PT Contoh Niaga',

  // --- UI primitives & misc ---
  'scan.chooseWarehouse': '— Pilih gudang tujuan —',
  'ui.showPassword': 'Lihat kata sandi',
  'ui.hidePassword': 'Sembunyikan kata sandi',
  'ui.show': 'Lihat',
  'ui.hide': 'Sembunyikan',

  // --- Remaining screen strings ---
  'account.name': 'Nama',
  'account.managedByAdmin': 'Nama, email dan peran diatur oleh admin organisasi Anda.',
  'mock.vat': 'PPN',
  'mock.disabled': 'Simulasi pembayaran tidak aktif. Atur BILLING_PROVIDER=mock di lingkungan pengembangan.',
  'scan.colActions': 'Aksi',
  'scan.poLineCount': '{count} baris barang pada PO ini.',
  'scan.multiMatchNote': 'Barcode ini cocok dengan beberapa baris PO — misalnya varian warna/ukuran, atau baris bonus. Pilih baris yang tepat.',
  'app.footer': 'DR Inbound · terhubung dengan Accurate Online',
  'verify.successSuffix': 'berhasil diverifikasi.',
  'verify.trialStartedAppend': ' Masa uji coba Anda sudah dimulai.',
  'verify.linkOnceNote': 'Tautan hanya berlaku sekali dan kedaluwarsa setelah 24 jam. Masuk ke akun Anda untuk meminta tautan baru.',
  'acc.edrcUpdated': 'Field ED RC untuk “{alias}” diperbarui menjadi {field}.',

  // --- Home & drafts inline strings ---
  'home.noDatabase': 'Belum ada database Accurate yang terhubung.',
  'home.openSettingsPre': 'Buka ',
  'home.settingsLink': 'Pengaturan → Koneksi Accurate',
  'home.openSettingsPost': ' untuk menghubungkan akun Accurate Anda.',
  'home.askAdminConnect': 'Hubungi admin perusahaan Anda untuk menghubungkannya.',
  'drafts.warehouseLabel': 'Gudang:',
  'drafts.lineSummary': '{lines} baris · {qty} kts di-scan',
  'drafts.byInline': 'oleh {name}',

  // --- Sweep 2 fixes ---
  'acc.dbConnected': 'Database “{alias}” berhasil dihubungkan.',
  'history.justPostedPre': 'Penerimaan ',
  'history.justPostedPost': ' berhasil dikirim ke Accurate.',

  // --- Billing plan tiers ---
  'billing.upgrade': 'Upgrade Paket',
  'billing.belowCurrent': 'Di bawah paket aktif Anda',
  'billing.upgradeHint': 'Kuota langsung naik; tagihan berikutnya memakai harga paket baru.',
  'err.billing.noDowngrade': 'Paket tidak bisa diturunkan dari paket aktif Anda. Untuk turun paket, hubungi tim DR Inbound.',

  // --- Forgot / reset password ---
  'ok.resetLinkSent': 'Jika email terdaftar, kami telah mengirim tautan atur ulang kata sandi.',
  'ok.passwordReset': 'Kata sandi berhasil diatur ulang. Silakan masuk dengan kata sandi baru Anda.',
  'err.reset.invalidLink': 'Tautan atur ulang kata sandi tidak valid atau sudah kedaluwarsa. Minta tautan baru.',
  'login.forgotLink': 'Lupa password?',
  'forgot.title': 'Lupa Kata Sandi',
  'forgot.intro': 'Masukkan email akun Anda. Jika terdaftar, kami kirim tautan untuk membuat kata sandi baru.',
  'forgot.submit': 'Kirim Tautan',
  'forgot.sending': 'Mengirim…',
  'forgot.sentTitle': 'Periksa Email Anda',
  'forgot.backToLogin': 'Kembali ke Masuk',
  'forgot.devNote': 'Mode pengembangan — tautan atur ulang ditampilkan di sini:',
  'forgot.failed': 'Gagal mengirim tautan. Coba lagi.',
  'reset.title': 'Atur Ulang Kata Sandi',
  'reset.intro': 'Buat kata sandi baru untuk akun Anda.',
  'reset.passwordHint': 'Minimal 10 karakter.',
  'reset.submit': 'Simpan Kata Sandi',
  'reset.doneTitle': 'Kata Sandi Diperbarui',
  'reset.toLogin': 'Ke Halaman Masuk',
  'reset.invalidTitle': 'Tautan Tidak Valid',
  'reset.noToken': 'Tautan tidak lengkap. Minta tautan baru dari halaman Lupa Kata Sandi.',
  'reset.failed': 'Gagal mengatur ulang kata sandi.',

  // --- Account profile edit & invoice table ---
  'account.editProfile': 'Ubah Profil',
  'account.profileSaved': 'Profil berhasil diperbarui.',
  'account.profileFailed': 'Gagal memperbarui profil.',
  'billing.downloadPdf': 'Unduh PDF',
  'billing.prev': 'Sebelumnya',
  'billing.next': 'Berikutnya',
  'billing.pageOf': 'Halaman {page} dari {total}',
  'err.v.tokenRequired': 'Token wajib diisi',
  'err.invoice.notPaid': 'Faktur hanya dapat diunduh setelah pembayaran lunas.',
} as const;
